import { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

export type Suggestion = { value: string; count: number }

type SuggestionPopupProps = {
  anchor: HTMLElement
  items: Suggestion[]
  // Counts are lower bounds while more records remain unseen → render as "<n>+".
  partial: boolean
  // Trailing "…" when more distinct values exist than shown, or the query has more pages.
  hasMore: boolean
  onPick: (value: string) => void
}

// Fixed-positioned, portaled popup so it is never clipped by the table's scroll container. Anchored
// under its input; position is captured on open (the parent closes it on scroll/resize rather than
// repositioning live, matching the reference behavior).
export function SuggestionPopup({ anchor, items, partial, hasMore, onPick }: SuggestionPopupProps) {
  const [rect, setRect] = useState<DOMRect | null>(null)

  useLayoutEffect(() => {
    setRect(anchor.getBoundingClientRect())
  }, [anchor])

  if (!rect) return null

  return createPortal(
    <div
      // z-index must clear Mantine's modal/overlay layer (200) so the popup shows
      // when SelectRecord is hosted inside a Modal, not just as a bare page.
      className="fixed max-w-[320px] rounded-lg border border-[#cdd4da] bg-white p-1 shadow-[0_1px_3px_rgba(16,24,40,0.08),0_8px_24px_rgba(16,24,40,0.06)]"
      style={{ left: rect.left, top: rect.bottom + 4, minWidth: rect.width, zIndex: 1000 }}
      role="listbox"
    >
      {items.map(({ value, count }) => (
        <div
          key={value}
          role="option"
          aria-selected={false}
          className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-2 py-1.5 text-xs hover:bg-[#eaf1ff]"
          // mousedown fires before the input's blur, so the value applies before the popup closes.
          onMouseDown={(e) => {
            e.preventDefault()
            onPick(value)
          }}
        >
          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[#1b2733]">
            {value}
          </span>
          <span className="flex-none text-[11px] text-[#64748b]">
            {count}
            {partial ? '+' : ''}
          </span>
        </div>
      ))}
      {hasMore && (
        <div
          className="cursor-default px-2 py-1.5 text-center text-xs text-[#64748b]"
          title="More matching values may exist (scroll the list to load more records)"
        >
          …
        </div>
      )}
    </div>,
    document.body,
  )
}
